import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ruler, Package, TrendingUp, Users, Scissors } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ProductManager } from "../components/ProductManager";

const STATUSES = ["paid", "in_production", "shipped", "delivered", "cancelled"];

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white border border-charcoal/10 rounded-2xl p-6" data-testid={testid}>
    <Icon size={20} strokeWidth={1.4} className="text-sunkissed mb-4" />
    <div className="font-serif text-3xl">{value}</div>
    <div className="overline text-charcoal/50 mt-1">{label}</div>
  </div>
);

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");

  const setOrderStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      setOrders((o) => o.map((x) => (x.order_id === id ? { ...x, status } : x)));
      toast.success(`Order ${id} → ${status.replace("_", " ")}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (user.role !== "admin") { setDenied(true); setBusy(false); return; }
    setBusy(true);
    Promise.all([
      api.get("/admin/stats").then((r) => setStats(r.data)),
      api.get("/admin/orders").then((r) => setOrders(r.data)),
      api.get("/admin/measurements").then((r) => setMeasurements(r.data)),
    ])
      .catch(() => setError("Could not load atelier data. Please refresh."))
      .finally(() => setBusy(false));
  }, [user, loading, navigate]);

  if (loading || busy) return <div className="pt-40 text-center text-charcoal/50" data-testid="admin-loading">Loading atelier…</div>;
  if (denied) return (
    <div className="pt-40 text-center min-h-screen" data-testid="admin-denied">
      <h1 className="font-serif text-3xl font-light">Atelier access only</h1>
      <p className="text-charcoal/55 mt-3">This area is reserved for the SHIOKOI team.</p>
    </div>
  );

  return (
    <div data-testid="admin-page" className="pt-28 pb-32 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-2">
          <Scissors size={20} className="text-sunkissed" />
          <div className="overline text-sunkissed">Atelier Dashboard</div>
        </div>
        <h1 className="font-serif text-4xl md:text-5xl font-light mb-10">Production Overview</h1>

        {error && <div className="mb-8 p-4 bg-destructive/10 text-destructive text-sm" data-testid="admin-error">{error}</div>}

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
            <StatCard icon={Package} label="Orders" value={stats.orders} testid="stat-orders" />
            <StatCard icon={TrendingUp} label="Revenue (CHF)" value={Number(stats.revenue).toFixed(0)} testid="stat-revenue" />
            <StatCard icon={Ruler} label="Fit Profiles" value={stats.measurements} testid="stat-measurements" />
            <StatCard icon={Users} label="Customers" value={stats.customers} testid="stat-customers" />
          </div>
        )}

        <div className="flex gap-3 mb-8 flex-wrap">
          {[["orders", "Orders"], ["measurements", "Fit Profiles"], ["products", "Products"]].map(([t, label]) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`overline px-6 py-3 rounded-full border transition-colors ${tab === t ? "bg-ocean text-sand border-ocean" : "border-charcoal/25 hover:border-charcoal"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "products" && <ProductManager />}

        {tab === "orders" && (
          <div className="bg-white border border-charcoal/10 rounded-2xl overflow-x-auto" data-testid="admin-orders">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-charcoal/10 text-left overline text-charcoal/50">
                  <th className="p-4">Order</th><th className="p-4">Customer</th><th className="p-4">Items · Size</th><th className="p-4">Total</th><th className="p-4">Status</th><th className="p-4">Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.order_id} className="border-b border-charcoal/5" data-testid={`admin-order-${o.order_id}`}>
                    <td className="p-4 font-medium">{o.order_id}</td>
                    <td className="p-4 text-charcoal/70">{o.customer?.name || "Guest"}<br /><span className="text-xs text-charcoal/45">{o.customer?.email || "—"}</span></td>
                    <td className="p-4 text-charcoal/70">{o.items?.map((i) => `${i.name} (${i.size || "—"}) ×${i.quantity}`).join(", ")}</td>
                    <td className="p-4">{(o.currency || "chf").toUpperCase()} {Number(o.amount).toFixed(2)}</td>
                    <td className="p-4">
                      <select
                        value={o.status}
                        onChange={(e) => setOrderStatus(o.order_id, e.target.value)}
                        data-testid={`order-status-${o.order_id}`}
                        className="border border-charcoal/20 rounded-lg px-2 py-1.5 bg-white text-xs capitalize focus:outline-none focus:border-charcoal"
                      >
                        {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                      </select>
                    </td>
                    <td className="p-4 text-charcoal/55">{new Date(o.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {orders.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-charcoal/50">No orders yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "measurements" && (
          <div className="bg-white border border-charcoal/10 rounded-2xl overflow-x-auto" data-testid="admin-measurements">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-charcoal/10 text-left overline text-charcoal/50">
                  <th className="p-4">Customer</th><th className="p-4">Gender</th><th className="p-4">Height</th><th className="p-4">Size</th><th className="p-4">Measurements (cm)</th><th className="p-4">Date</th>
                </tr>
              </thead>
              <tbody>
                {measurements.map((m) => (
                  <tr key={m.id} className="border-b border-charcoal/5" data-testid={`admin-measure-${m.id}`}>
                    <td className="p-4 text-charcoal/70">{m.customer?.name || "Guest"}<br /><span className="text-xs text-charcoal/45">{m.customer?.email || "—"}</span></td>
                    <td className="p-4 capitalize">{m.gender}</td>
                    <td className="p-4">{m.height_cm} cm</td>
                    <td className="p-4 font-medium">{m.recommended_size}</td>
                    <td className="p-4 text-charcoal/70 text-xs max-w-md">
                      {Object.entries(m.measurements_cm || {}).map(([k, v]) => `${k.replace(/_/g, " ")} ${v}`).join(" · ")}
                    </td>
                    <td className="p-4 text-charcoal/55">{new Date(m.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {measurements.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-charcoal/50">No fit profiles yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
